/**
 * Snapshot WebSocket client. Subscribes to the bridge's JSON snapshot
 * channel (path "/") and surfaces three things the voice page needs:
 *
 *   1. roster      - everyone on the server, with their AGID + display name
 *                    + whether they're currently in voice
 *   2. audio gains - per-sender L/R gains, keyed by AGID, refreshed every
 *                    snapshot tick (driven by in-game distance + bearing)
 *   3. voice sessions - sessionId <-> AGID + active state, so when a binary
 *                       AudioFwd frame arrives on the voice WS we already
 *                       know which player produced it
 *
 * Also pushes personalMuteReplace messages back to the bridge when the
 * user toggles a mute, so the bridge can drop frames at the sender side
 * (bidirectional muting). Mirrors `mobile/lib/src/snapshot_client.dart`
 * and `client/src/AshenAgeVoice.Core/Net/SnapshotClient.cs`.
 */

import { refreshSession } from '../api.ts'
import { baseWsOrigin, getRefreshToken } from '../auth.ts'

const HEARTBEAT_INTERVAL_MS = 20_000
const RECONNECT_BASE_MS = 1_000
const RECONNECT_MAX_MS = 30_000

export interface RosterEntry {
  readonly agid: string
  readonly name: string
  readonly voiceConnected: boolean
}

export interface AudioGain {
  readonly agid: string
  readonly name: string
  readonly gainL: number
  readonly gainR: number
  /**
   * Listener-local sender position in cm (+X = right, +Y = forward, +Z = up).
   * Optional because old bridges (pre-2026-05-23) don't send it; consumers
   * that need 3D positioning (PannerNode HRTF) MUST guard for undefined and
   * fall back to gainL/gainR-driven stereo when absent.
   */
  readonly localX?: number
  readonly localY?: number
  readonly localZ?: number
  /** 3D Euclidean distance to the sender (cm). Optional; see above. */
  readonly distanceCm?: number
}

export interface SnapshotFrame {
  readonly type: 'snapshot'
  readonly serverId: string
  readonly tick: number
  readonly takenAt: string
  readonly rFullCm: number
  readonly rMaxCm: number
  readonly audio: ReadonlyArray<AudioGain>
  readonly roster: ReadonlyArray<RosterEntry>
}

export interface VoiceSessionEvent {
  readonly type: 'voiceSession'
  readonly sessionId: number
  readonly linkId: string
  readonly agid: string
  readonly active: boolean
}

export interface HelloMessage {
  readonly type: 'hello'
  readonly linkId: string
  readonly agid: string
}

export interface SnapshotConnectionOptions {
  readonly serverId: string
  readonly onHello: (msg: HelloMessage) => void
  readonly onSnapshot: (frame: SnapshotFrame) => void
  readonly onVoiceSession: (evt: VoiceSessionEvent) => void
  readonly onAuthFailed: () => void
  readonly onState: (state: SnapshotState) => void
}

export type SnapshotState =
  | { kind: 'connecting' }
  | { kind: 'connected' }
  | { kind: 'reconnecting'; attempt: number; nextDelayMs: number }
  | { kind: 'stopped' }

export class SnapshotConnection {
  private readonly opts: SnapshotConnectionOptions
  private ws: WebSocket | null = null
  private pingTimer: ReturnType<typeof setInterval> | null = null
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null
  private reconnectAttempt = 0
  private stopped = false
  private mutedLinkIds: ReadonlyArray<string> = []

  constructor(opts: SnapshotConnectionOptions) {
    this.opts = opts
  }

  async start(): Promise<void> {
    this.stopped = false
    await this.connectOnce()
  }

  stop(): void {
    this.stopped = true
    if (this.pingTimer !== null) { clearInterval(this.pingTimer); this.pingTimer = null }
    if (this.reconnectTimer !== null) { clearTimeout(this.reconnectTimer); this.reconnectTimer = null }
    if (this.ws !== null) {
      try { this.ws.close(1000, 'client stopped') } catch { /* socket may be dead */ }
      this.ws = null
    }
    this.opts.onState({ kind: 'stopped' })
  }

  /**
   * Replace the personal mute list. Bridge enforces sender-side drop so
   * muting someone also stops them from hearing us. Caller passes linkIds,
   * not AGIDs, because the bridge keys session muting by linkId.
   */
  setPersonalMutes(linkIds: ReadonlyArray<string>): void {
    this.mutedLinkIds = linkIds.slice()
    this.sendJson({ type: 'personalMuteReplace', mutedLinkIds: this.mutedLinkIds })
  }

  private async connectOnce(): Promise<void> {
    this.opts.onState({ kind: 'connecting' })

    const refreshToken = getRefreshToken()
    if (refreshToken === null) { this.opts.onAuthFailed(); return }

    let sessionToken: string
    try {
      const res = await refreshSession(refreshToken)
      sessionToken = res.sessionToken
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err)
      if (msg.includes('invalid_token') || msg.includes('http_401')) {
        this.opts.onAuthFailed()
        return
      }
      this.scheduleReconnect()
      return
    }

    const url = `${baseWsOrigin()}/?token=${encodeURIComponent(sessionToken)}`
    const ws = new WebSocket(url)
    this.ws = ws

    ws.addEventListener('open', () => {
      this.reconnectAttempt = 0
      this.opts.onState({ kind: 'connected' })
      // Server expects a subscribe message before it starts pushing
      // snapshot frames. After subscribe the bridge sends an initial
      // serverSettingsUpdated frame too; we don't use those settings yet.
      this.sendJson({ type: 'subscribe', serverID: this.opts.serverId })
      // Re-push mute list on every (re)connect; bridge drops it on socket
      // close so a reconnect starts with empty mute state.
      if (this.mutedLinkIds.length > 0) {
        this.sendJson({ type: 'personalMuteReplace', mutedLinkIds: this.mutedLinkIds })
      }
      this.pingTimer = setInterval(() => {
        this.sendJson({ type: 'ping' })
      }, HEARTBEAT_INTERVAL_MS)
    })

    ws.addEventListener('message', (event) => {
      if (typeof event.data !== 'string') return
      let parsed: unknown
      try { parsed = JSON.parse(event.data) } catch { return }
      this.handleParsed(parsed)
    })

    ws.addEventListener('close', (event) => {
      if (this.pingTimer !== null) { clearInterval(this.pingTimer); this.pingTimer = null }
      this.ws = null
      if (this.stopped) return
      if (event.code === 4002 || event.code === 4001) { this.opts.onAuthFailed(); return }
      // 4007 = bridge rejected us because another connection for the same
      // linkId is already active. Tight retries here previously fed a
      // kick-reconnect flap with the incumbent. Back off long enough for
      // the other side to close cleanly if the user shut their other tab.
      if (event.code === 4007) {
        this.scheduleReconnect(30_000)
        return
      }
      this.scheduleReconnect()
    })

    ws.addEventListener('error', () => {
      // Close handler owns reconnect.
    })
  }

  private handleParsed(parsed: unknown): void {
    if (typeof parsed !== 'object' || parsed === null) return
    const v = parsed as Record<string, unknown>
    if (v.type === 'hello' && typeof v.linkId === 'string' && typeof v.agid === 'string') {
      this.opts.onHello({ type: 'hello', linkId: v.linkId, agid: v.agid })
      return
    }
    if (v.type === 'snapshot') {
      const frame = parseSnapshot(v)
      if (frame !== null) this.opts.onSnapshot(frame)
      return
    }
    if (v.type === 'voiceSession' &&
        typeof v.sessionId === 'number' &&
        typeof v.linkId === 'string' &&
        typeof v.agid === 'string' &&
        typeof v.active === 'boolean') {
      this.opts.onVoiceSession({
        type: 'voiceSession',
        sessionId: v.sessionId,
        linkId: v.linkId,
        agid: v.agid,
        active: v.active,
      })
      return
    }
    // pong, serverSettingsUpdated, subscribeError: ignored for now.
  }

  private sendJson(payload: unknown): void {
    const ws = this.ws
    if (ws === null || ws.readyState !== WebSocket.OPEN) return
    try { ws.send(JSON.stringify(payload)) } catch { /* socket already closed */ }
  }

  private scheduleReconnect(fixedDelayMs?: number): void {
    if (this.stopped) return
    this.reconnectAttempt += 1
    const delay = fixedDelayMs ?? Math.min(
      RECONNECT_BASE_MS * 2 ** Math.min(this.reconnectAttempt - 1, 5),
      RECONNECT_MAX_MS,
    )
    this.opts.onState({
      kind: 'reconnecting',
      attempt: this.reconnectAttempt,
      nextDelayMs: delay,
    })
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null
      void this.connectOnce()
    }, delay)
  }
}

/**
 * Safely parse a snapshot payload. Returns null if any required field is
 * missing or wrong-typed. Exported for unit testing.
 */
export function parseSnapshot(v: Record<string, unknown>): SnapshotFrame | null {
  if (typeof v.serverId !== 'string') return null
  if (typeof v.tick !== 'number') return null
  if (typeof v.takenAt !== 'string') return null
  if (typeof v.rFullCm !== 'number' || typeof v.rMaxCm !== 'number') return null
  if (!Array.isArray(v.audio) || !Array.isArray(v.roster)) return null

  const audio: AudioGain[] = []
  for (const a of v.audio) {
    if (typeof a !== 'object' || a === null) continue
    const o = a as Record<string, unknown>
    if (typeof o.agid !== 'string' || typeof o.name !== 'string') continue
    if (typeof o.gainL !== 'number' || typeof o.gainR !== 'number') continue
    const entry: AudioGain = {
      agid: o.agid,
      name: o.name,
      gainL: o.gainL,
      gainR: o.gainR,
    }
    // 3D positional fields are opt-in - older bridges don't send them, in
    // which case PannerNode-based clients fall back to the stereo gains.
    if (typeof o.localX === 'number') (entry as { localX?: number }).localX = o.localX
    if (typeof o.localY === 'number') (entry as { localY?: number }).localY = o.localY
    if (typeof o.localZ === 'number') (entry as { localZ?: number }).localZ = o.localZ
    if (typeof o.distanceCm === 'number') (entry as { distanceCm?: number }).distanceCm = o.distanceCm
    audio.push(entry)
  }

  const roster: RosterEntry[] = []
  for (const r of v.roster) {
    if (typeof r !== 'object' || r === null) continue
    const o = r as Record<string, unknown>
    if (typeof o.agid !== 'string' || typeof o.name !== 'string') continue
    roster.push({
      agid: o.agid,
      name: o.name,
      voiceConnected: o.voiceConnected === true,
    })
  }

  return {
    type: 'snapshot',
    serverId: v.serverId,
    tick: v.tick,
    takenAt: v.takenAt,
    rFullCm: v.rFullCm,
    rMaxCm: v.rMaxCm,
    audio,
    roster,
  }
}
